import { Link } from "react-router-dom";
import clsx from "clsx";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Spinner } from "@/components/ui/Spinner";
import type { ReadyMaterialItem } from "@/lib/material";
import { maFortschrittLabel } from "@/pages/material/maListDisplay";
import type { LieferscheinSummary } from "@/lib/lieferscheine";
import type { TimesheetSummary } from "@/lib/timesheets";
import {
  formatLieferscheinProjectLabel,
  getLieferscheinContactAgeInfo,
  getLieferscheinContactName,
} from "@/pages/lieferscheine/lieferscheinListLogic";
import { TimesheetBadge } from "@/pages/stundennachweise/TimesheetBadge";
import {
  getTimesheetDisplayNumber,
  timesheetStatusLabel,
  getTimesheetStatus,
} from "@/pages/stundennachweise/timesheetFilterLogic";

export interface HomeKpiItem {
  key: string;
  label: string;
  value: string;
  href?: string;
  title?: string;
  alert?: boolean;
}

const OVERVIEW_LINK_CLASS =
  "inline-flex items-center rounded-full border border-accent px-3 py-1 text-xs font-medium text-accent hover:bg-accent/10";

export function HomeKpiStrip({ items }: { items: HomeKpiItem[] }) {
  if (!items.length) return null;
  return (
    <Card bodyClassName="!p-4">
      <div className="eh-home-dashboard-strip">
        <div className="eh-home-dashboard-strip__metrics">
          {items.map((item) => (
            <HomeKpi key={item.key} item={item} />
          ))}
        </div>
      </div>
    </Card>
  );
}

function HomeKpi({ item }: { item: HomeKpiItem }) {
  const body = (
    <>
      <span className="eh-home-metric__label">{item.label}</span>
      <strong className={clsx("eh-home-metric__value", item.alert && "text-status-red")}>
        {item.value}
      </strong>
    </>
  );
  if (!item.href) {
    return (
      <div className="eh-home-metric" title={item.title}>
        {body}
      </div>
    );
  }
  if (item.href.startsWith("#")) {
    return (
      <a className="eh-home-metric" href={item.href} title={item.title}>
        {body}
      </a>
    );
  }
  return (
    <Link className="eh-home-metric" to={item.href} title={item.title}>
      {body}
    </Link>
  );
}

function CountBadge({ count, tone = "red" }: { count: number; tone?: "red" | "orange" }) {
  if (count <= 0) return null;
  return (
    <span
      className={clsx(
        "rounded-full px-2 py-0.5 text-xs font-semibold",
        tone === "orange"
          ? "bg-status-orange/15 text-status-orange"
          : "bg-status-red/15 text-status-red",
      )}
    >
      {count} offen
    </span>
  );
}

function QueueState({
  loading,
  error,
  empty,
  emptyText,
}: {
  loading: boolean;
  error: string | null;
  empty: boolean;
  emptyText: string;
}) {
  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }
  if (error) {
    return <p className="px-4 py-6 text-sm text-status-red">{error}</p>;
  }
  if (empty) {
    return <p className="px-4 py-6 text-sm text-label-secondary">{emptyText}</p>;
  }
  return null;
}

function formatContactSince(value?: string | null, now = Date.now()): string {
  const parsed = value ? new Date(value) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) return "";
  const days = Math.floor((now - parsed.getTime()) / (24 * 60 * 60 * 1000));
  if (days <= 0) return "seit heute";
  if (days === 1) return "seit 1 Tag";
  return `seit ${days} Tagen`;
}

export function LieferscheineAtContactCard({
  rows,
  loading,
  error,
  onOpen,
}: {
  rows: LieferscheinSummary[];
  loading: boolean;
  error: string | null;
  onOpen: (dokumentId: number) => void;
}) {
  const state = (
    <QueueState
      loading={loading}
      error={error}
      empty={!rows.length}
      emptyText="Keine Lieferscheine beim Ansprechpartner."
    />
  );

  return (
    <Card
      id="home-lieferscheine"
      className="eh-home-anchor"
      title={
        <span className="inline-flex flex-wrap items-center gap-2">
          Lieferscheine beim Ansprechpartner
          <CountBadge count={loading || error ? 0 : rows.length} />
        </span>
      }
      actions={
        <Link to="/dokumente/lieferscheine" className={OVERVIEW_LINK_CLASS}>
          Zur Übersicht
        </Link>
      }
      bodyClassName="!p-0"
    >
      {state ?? (
        <ul className="eh-home-queue eh-home-queue-scroll" role="list">
          {rows.map((row) => (
            <li key={row.id}>
              <button type="button" onClick={() => onOpen(row.id)} className="eh-home-queue-row">
                <span className="eh-home-queue-row__kicker">
                  {row.display_number || `#${row.id}`}
                </span>
                <span className="eh-home-queue-row__copy">
                  <span className="eh-home-queue-row__title">
                    {formatLieferscheinProjectLabel(row) || row.kunde || "—"}
                  </span>
                  <span className="eh-home-queue-row__meta">{getLieferscheinContactName(row)}</span>
                  <ContactAge row={row} />
                </span>
                <ChevronRightIcon className="eh-home-queue-row__go" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function ContactAge({ row }: { row: LieferscheinSummary }) {
  const since = formatContactSince(row.at_contact_at);
  const aging = getLieferscheinContactAgeInfo(row);
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5 text-xs text-label-secondary">
      <span>{since || "beim Ansprechpartner"}</span>
      {aging ? (
        <TimesheetBadge compact className={aging.threshold.className}>
          {aging.threshold.label}
        </TimesheetBadge>
      ) : null}
    </span>
  );
}

interface WorkRow {
  id: string;
  kicker: string;
  title: string;
  meta: string;
  status?: string;
  onOpen: () => void;
}

function WorkQueueCard({
  id,
  title,
  count,
  tone = "red",
  href,
  linkLabel,
  rows,
  loading,
  error,
  emptyText,
}: {
  id?: string;
  title: string;
  count: number;
  tone?: "red" | "orange";
  href: string;
  linkLabel: string;
  rows: WorkRow[];
  loading: boolean;
  error: string | null;
  emptyText: string;
}) {
  const state =
    rows.length > 0 ? null : (
      <QueueState loading={loading} error={error} empty emptyText={emptyText} />
    );
  return (
    <Card
      id={id}
      className="eh-home-anchor"
      title={
        <span className="inline-flex flex-wrap items-center gap-2">
          {title}
          <CountBadge count={loading || (error && rows.length === 0) ? 0 : count} tone={tone} />
        </span>
      }
      actions={
        <Link to={href} className={OVERVIEW_LINK_CLASS}>
          {linkLabel}
        </Link>
      }
      bodyClassName="!p-0"
    >
      {state ?? (
        <ul className="eh-home-queue eh-home-queue-scroll" role="list">
          {rows.map((row) => (
            <li key={row.id}>
              <button type="button" onClick={row.onOpen} className="eh-home-queue-row">
                {row.kicker ? (
                  <span className="eh-home-queue-row__kicker">{row.kicker}</span>
                ) : null}
                <span className="eh-home-queue-row__copy">
                  <span className="eh-home-queue-row__title">{row.title}</span>
                  <span className="eh-home-queue-row__meta">{row.meta}</span>
                </span>
                {row.status ? (
                  <span className="eh-home-queue-row__status">{row.status}</span>
                ) : null}
                <ChevronRightIcon className="eh-home-queue-row__go" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function timesheetWorkRow(
  ts: TimesheetSummary,
  onOpen: () => void,
): WorkRow {
  const project = String(ts.auftrag_display || ts.auftragsnummer || "").trim();
  const meta = [ts.kunde, ts.ansprechpartner]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(" · ");
  return {
    id: String(ts.id),
    kicker: getTimesheetDisplayNumber(ts) || `#${ts.id}`,
    title: project || `Nachweis #${ts.id}`,
    meta: meta || "—",
    status: timesheetStatusLabel(getTimesheetStatus(ts)),
    onOpen,
  };
}

function lieferscheinWorkRow(
  row: LieferscheinSummary,
  onOpen: () => void,
): WorkRow {
  const meta = [row.kunde, getLieferscheinContactName(row)].filter(Boolean).join(" · ");
  return {
    id: String(row.id),
    kicker: row.display_number || `#${row.id}`,
    title: formatLieferscheinProjectLabel(row) || row.kunde || `Lieferschein #${row.id}`,
    meta: meta || "—",
    onOpen,
  };
}

export function BillingHomeCard({
  kind,
  signed,
  drafts,
  questions,
  lieferscheine,
  loading,
  error,
  onOpenTimesheet,
  onOpenLieferschein,
}: {
  kind: "signed" | "lieferscheine" | "drafts" | "questions";
  signed: TimesheetSummary[];
  drafts: TimesheetSummary[];
  questions: TimesheetSummary[];
  lieferscheine: LieferscheinSummary[];
  loading: boolean;
  error: string | null;
  onOpenTimesheet: (timesheetId: number) => void;
  onOpenLieferschein: (dokumentId: number, auftragId?: number | null) => void;
}) {
  const toTimesheetRows = (rows: TimesheetSummary[]): WorkRow[] =>
    rows.map((ts) => timesheetWorkRow(ts, () => onOpenTimesheet(ts.id)));
  const toLieferscheinRows = (rows: LieferscheinSummary[]): WorkRow[] =>
    rows.map((row) => lieferscheinWorkRow(row, () => onOpenLieferschein(row.id, row.auftrag_id)));

  if (kind === "signed") {
    return (
      <WorkQueueCard
        id="home-billing-signed"
        title="Unterschriebene Stundennachweise"
        count={signed.length}
        href="/dokumente/stundennachweise"
        linkLabel="Zu Stundennachweisen"
        rows={toTimesheetRows(signed)}
        loading={loading}
        error={error}
        emptyText="Keine unterschriebenen Stundennachweise."
      />
    );
  }
  if (kind === "lieferscheine") {
    return (
      <WorkQueueCard
        id="home-billing-lieferscheine"
        title="Neue Lieferscheine"
        count={lieferscheine.length}
        href="/dokumente/lieferscheine"
        linkLabel="Zu Lieferscheinen"
        rows={toLieferscheinRows(lieferscheine)}
        loading={loading}
        error={error}
        emptyText="Keine neuen Lieferscheine für die Abrechnung."
      />
    );
  }
  if (kind === "drafts") {
    return (
      <WorkQueueCard
        id="home-billing-drafts"
        title="Rechnungsentwürfe"
        count={drafts.length}
        tone="orange"
        href="/dokumente/stundennachweise"
        linkLabel="Zu Stundennachweisen"
        rows={toTimesheetRows(drafts)}
        loading={loading}
        error={error}
        emptyText="Keine offenen Rechnungsentwürfe."
      />
    );
  }
  return (
    <WorkQueueCard
      id="home-billing-questions"
      title="Nachweise mit Rückfrage"
      count={questions.length}
      tone="orange"
      href="/dokumente/stundennachweise"
      linkLabel="Zu Stundennachweisen"
      rows={toTimesheetRows(questions)}
      loading={loading}
      error={error}
      emptyText="Keine Stundennachweise mit Rückfrage."
    />
  );
}

export interface OfficeHomeCounts {
  materialOpen: number;
  materialUrgent: number;
  materialInProgress: number;
  feinplanung: number;
  proposals: number;
  angebote: number;
  mahnungen: number;
  anfragen: number;
}

export function OfficeOpenPoints({
  counts,
  loading,
  error,
}: {
  counts: OfficeHomeCounts | null;
  loading: boolean;
  error: string | null;
}) {
  const items = counts
    ? [
        {
          key: "urgent",
          label: "Fixtermine Material",
          count: counts.materialUrgent,
          href: "/bestellwesen/materialanforderungen",
          urgent: true,
        },
        {
          key: "material",
          label: "Materialanforderungen offen",
          count: Math.max(0, counts.materialOpen - counts.materialUrgent),
          href: "/bestellwesen/materialanforderungen",
          urgent: false,
        },
        {
          key: "progress",
          label: "Material in Bearbeitung",
          count: counts.materialInProgress,
          href: "/bestellwesen/materialanforderungen",
          urgent: false,
        },
        {
          key: "fein",
          label: "Feinplanung angefragt",
          count: counts.feinplanung,
          href: "/montageplanung",
          urgent: false,
        },
        {
          key: "proposals",
          label: "Vorschläge von Monteuren",
          count: counts.proposals,
          href: "/montageplanung",
          urgent: false,
        },
        {
          key: "angebote",
          label: "Angebotsanfragen offen",
          count: counts.angebote,
          href: "/angebotsanfragen",
          urgent: false,
        },
        {
          key: "mahnungen",
          label: "Mahnungen fällig",
          count: counts.mahnungen,
          href: "/dokumente/mahnwesen",
          urgent: true,
        },
        {
          key: "anfragen",
          label: "Anfragen mit Antwort",
          count: counts.anfragen,
          href: "/bestellwesen/anfragen",
          urgent: false,
        },
      ].filter((item) => item.count > 0)
    : [];

  return (
    <Card
      title="Offene Punkte"
      description="Nur das, was gerade zählt."
      bodyClassName="!p-0"
    >
      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : error ? (
        <p className="px-4 py-6 text-sm text-status-red">{error}</p>
      ) : items.length === 0 ? (
        <p className="px-4 py-6 text-sm text-label-secondary">Keine offenen Punkte.</p>
      ) : (
        <ul className="eh-home-queue eh-home-queue-scroll" role="list">
          {items.map((item) => (
            <li key={item.key}>
              <Link to={item.href} className="eh-home-queue-row">
                <span className="eh-home-queue-row__copy">
                  <span className="eh-home-queue-row__title">{item.label}</span>
                </span>
                <span
                  className={clsx(
                    "eh-home-queue-row__status eh-home-queue-row__status--show",
                    item.urgent ? "!bg-status-red/15 !text-status-red" : "!bg-status-orange/15 !text-status-orange",
                  )}
                >
                  {item.count}
                </span>
                <ChevronRightIcon className="eh-home-queue-row__go" aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function formatReadyPosition(row: {
  menge?: number | null;
  einheit?: string | null;
  text?: string | null;
}): string {
  const qty = [row.menge, row.einheit]
    .map((part) => (part == null ? "" : String(part).trim()))
    .filter(Boolean)
    .join(" ");
  const text = String(row.text || "").trim();
  return [qty, text].filter(Boolean).join(" ") || "Position";
}

export function FieldMaterialCard({
  items,
  loading,
  error,
  markingId,
  onMarkSeen,
}: {
  items: ReadyMaterialItem[] | null;
  loading: boolean;
  error: string | null;
  markingId: number | null;
  onMarkSeen: (id: number) => void;
}) {
  const count = items?.length ?? 0;
  const empty = !loading && !error && count === 0;
  return (
    <Card
      title={
        <span className="inline-flex flex-wrap items-center gap-2">
          Material bereit
          {count > 0 ? (
            <span className="rounded-full bg-status-green/15 px-2 py-0.5 text-xs font-semibold text-status-green">
              {count} neu
            </span>
          ) : null}
        </span>
      }
      actions={
        <Link to="/materialanforderungen" className={OVERVIEW_LINK_CLASS}>
          Zu Materialanforderungen
        </Link>
      }
      bodyClassName="!p-0"
    >
      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : error && count === 0 ? (
        <p className="px-4 py-6 text-sm text-status-red">{error}</p>
      ) : empty ? (
        <p className="px-4 py-6 text-sm text-label-secondary">Kein bereitgelegtes Material.</p>
      ) : (
        <ul className="eh-home-queue eh-home-queue-scroll" role="list">
          {error ? <li className="px-2 text-xs text-status-red">{error}</li> : null}
          {(items ?? []).map((item) => {
            const positions = item.position_preview ?? [];
            const hidden = Math.max(0, Number(item.position_count || 0) - positions.length);
            const project = [item.auftrag_nummer, item.auftrag_beschreibung]
              .map((part) => String(part || "").trim())
              .filter(Boolean)
              .join(" · ");
            return (
              <li key={item.id} className="eh-home-queue-card">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-label">
                      {item.ma_nummer || `MA #${item.id}`}
                      {item.title ? ` · ${item.title}` : ""}
                    </p>
                    {project ? (
                      <p className="truncate text-xs text-label-secondary">{project}</p>
                    ) : null}
                    {item.auftrag_ort ? (
                      <p className="truncate text-xs text-label-tertiary">{item.auftrag_ort}</p>
                    ) : null}
                  </div>
                  <span className="rounded-full bg-status-green/15 px-2 py-0.5 text-xs font-medium text-status-green">
                    {maFortschrittLabel(
                      item.monteur_fortschritt === "geliefert" ? "geliefert" : "bereitgelegt",
                    )}
                  </span>
                </div>
                {positions.length > 0 ? (
                  <ul className="space-y-0.5 text-sm text-label">
                    {positions.map((position, index) => (
                      <li key={`${item.id}-${index}`}>{formatReadyPosition(position)}</li>
                    ))}
                    {hidden > 0 ? (
                      <li className="text-xs text-label-secondary">
                        {hidden === 1 ? "1 weitere Position" : `${hidden} weitere Positionen`}
                      </li>
                    ) : null}
                  </ul>
                ) : (
                  <p className="text-sm text-label-secondary">Keine Positionen hinterlegt.</p>
                )}
                <div>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    loading={markingId === item.id}
                    disabled={markingId != null && markingId !== item.id}
                    onClick={() => onMarkSeen(item.id)}
                  >
                    Als gelesen
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
